// janus-engine/ability/summon.ts — the SUMMON CAST lane in the browser (Wizard v9 WP3, owner 2026-10-09: "new summon rules live only in TS").
//
// Descriptors remain internal to the resolver. The spawn planner expands them into complete saved rows before ability commit;
// PHP accepts the fixed row contract and persists it. Turn-owner dispatches use the same browser planner.
//
// Served shapes (catalog census 2026-10-09, tools/probes/wizard_v9_summon_shapes_v1.php — 263 items): a single `summon` creature or a
// `user_choice_1_summon` pick; no roll or a plain Summon Roll (count = the roll); with / without monster template, summon type, duration,
// concentration; a ground point (FT3D summon placement, one spot per summon, the rest spread around it) or a creature target; a corpse-
// raiser on a dead creature row. Named hand-backs: `summon:multi-creature-list` (6 items author several `summon` rows), `summon:user-choice-
// 2-4`, `summon:caster-makes-summon-
// roll-trigger` (a triggers_when_caster_makes_roll_type [Summon Roll] carrier on the caster), `summon:point-target-gate`,
// a damaging summon (`post-damage-follow-up`), a corpse PILE / object target (`janus:non-creature-target`).
import { type JanusCtx, type Row, activeStatuses, ecId, ecLean, handBack, itemField, record, refuse, scalar, upper, definitionNotHeld } from './context.js';
import { type JanusInput } from './janus.js';
import { getStatusEffectDurationTimes } from './statuses/get-status-effect-duration-times.js';
import { removeStoredStatus } from './statuses/status-effect-removal-helpers.js';
import { layerListEntryUuid } from './statuses/status-trigger-gate.js';
import { runTriggerFamily, triggerCall, triggerFamilyPreDraw } from './trigger-executor.js';
import { type TriggerEvent, collectTriggerCandidates } from '../reactions/trigger-dispatch.js';
import { janusCtxTriggerReaders } from '../reactions/janus-ctx-trigger-readers.js';
import { CT_GATE_KEYS, creatureTypeGateAllows } from './creature-type-gate.js';

export const SUMMON_LAYERS: readonly string[] = ['summon', 'user_choice_1_summon', 'user_choice_2_summon', 'user_choice_3_summon', 'user_choice_4_summon'];
/** RollType::getId('summon_roll'). */
export const SUMMON_ROLL = 'E5016E3A-6A12-2287-5F48-5CFAC863E967';
const CMRT = 'triggers_when_caster_makes_roll_type';
const FRIEND = '7DBEB6D4-D147-703A-022E-05B290BB95C3';
/** StatusEffect 'dead' (vitality_status). */
const DEAD_STATUS = '9FEE2E8A-9CF7-D74D-5D16-B86C2603A1FA';
/** FT_SUMMON_POINTS_MAX (janus_summon.php / FT3DAOEPlacement.ts): the most summons one cast positions individually. */
export const SUMMON_POINTS_MAX = 50;
/** SizeType base_size (get_size_dimensions width); an unknown id reads Medium. */
const SIZE_WIDTH_FT: Record<string, number> = { '13456FEC-A2EE-DB46-5829-0EB2F627E1B7': 2.5, 'AAC1F466-7A8B-C593-1ADC-FC402B8909D7': 3.75,
  'DC23AA88-CB3D-EA4E-F6F7-6389ABC4F9DC': 5, '52FFDB16-A991-C8D4-FE59-7C75EE6CF7AD': 10, 'D0EF6D7A-82BC-1E6E-9D36-5004C7E0561C': 15,
  'CEA2946E-D395-46EA-A6E7-74D21EA2376E': 20, 'C94CDD74-8ACD-26B0-5C53-4CA29CF21D68': 25, '4C33FD22-5382-15DA-1C46-F57F14378AFD': 30 };
/** RangeType x_distance (includes/additions.php RangeType::LIST) — the ranges a fallback spot is measured against. */
const RANGE_FT: Record<string, number> = { '4C761395-2532-3F23-7CBA-CA81AF5C0855': 30, 'A95F219A-6288-6B17-6DB7-03DE4B97AE96': 60,
  'D1E8DA38-61C2-97AA-6173-4BF6157FEEE3': 120, 'F6DAE84B-CBB3-5C0F-89EF-370DE841DFDA': 240 };
const UUID = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;

export interface SummonPoint { x: number; y: number; z: number }
/** Internal descriptor for ONE summoned creature; expanded into spawnRows before commit. */
export interface SummonPlan {
  /** A nested browser dispatch owns its own caster and duration origin. */
  caster_id?: string;
  ability_id?: string;
  /** The creature template (items.item_id) that rises. */
  item_id: string;
  monster_template_id: string | null;
  /** The ability's summon_type (the key avoids the layer name: the commit is a PHP rule file the layer lint scans). */
  summon_type_id: string | null;
  /** The summoner's side (encounter_row_minion_team_uuid); '' = the template's default team. */
  team: string;
  /** Where it stands (x / y floor plane, z elevation). null = a corpse-raise: it rises where the body lies. */
  point: SummonPoint | null;
  /** The DEAD encounter creature this summon raises (summon_transforms_targeted_corpse) — consumed by the raise. */
  corpse: string | null;
  /** The summon-tag status (concentration, duration or maintenance): its end, its four turn flags and the use's connection (a dropped
   *  concentration removes it with the rest of the connection — the summon despawns). null = a permanent summon. */
  tag: { ends: string; flags: [number, number, number, number]; connection: string } | null;
  /** janus_initialize_summoned_creature_ether's branch: in combat the Ether pool starts empty, outside it starts full. */
  in_combat: 0 | 1;
  /** How many of this use's log rows precede its summoned_creature row (PHP logs it from inside post_effect_conditions) — the commit
   *  persists the summon at that point among the cast's rows. */
  after_row: number;
  /** One source per ground cast, on the first real creature; every tag shares its connection. */
  maintaining_source?: true;
}
/** The page's summon placement for THIS cast (FT3D summon placement): the cast point and the spots banked one per summon. */
export interface SummonRequest { point: SummonPoint; points: SummonPoint[]; /** the user_choice_1_summon pick, in its own field (a charged cast's
 *  mode and source ride use_ability_variable_1 / _2). */ pick?: string }

const finite = (v: unknown): boolean => typeof v === 'number' && Number.isFinite(v) && Math.abs(v) <= 1000000;
export function validSummonPoint(p: unknown): p is SummonPoint {
  const r = record(p);
  return !!r && finite(r.x) && finite(r.y) && finite(r.z);
}

export function isSummonAbility(ctx: JanusCtx, ability: Row): boolean {
  return SUMMON_LAYERS.some((key) => ctx.read.has(ability, key));
}

/** The ground point a summon placement aims at — PHP's encounter-blob target with the placement's coordinates. Never a member. */
export function summonPointTarget(encounterId: string, point: SummonPoint): Row {
  return { ft_summon_point: true, encounters: { encounter_id: upper(encounterId) },
    position: { x_pos: point.x, y_pos: point.y, z_pos: point.z } };
}
export const isSummonPointTarget = (row: Row | null | undefined): boolean => record(row)?.ft_summon_point === true;

function position(row: Row): SummonPoint | null {
  const p = record(row.position) ?? record(row.pos_arr) ?? row;
  const read = (key: string): number | null => {
    const v = scalar(p[key]);
    if (v === undefined || v === null || v === '') return 0;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const x = read('x_pos'), y = read('y_pos'), z = read('z_pos');
  return x === null || y === null || z === null ? null : { x, y, z };
}

/** The creature a cast summons: the fixed `summon`, or the caster's `user_choice_1_summon` pick (use_ability_variable_1, else _2 — PHP's
 *  janus_summon order). Only a creature the ability offers. */
export function summonCreatureId(ctx: JanusCtx, input: JanusInput, ability: Row): string {
  if (ctx.read.entries(ability, 'summon').length > 1) handBack('summon:multi-creature-list');
  if (['user_choice_2_summon', 'user_choice_3_summon', 'user_choice_4_summon'].some((key) => ctx.read.has(ability, key))) handBack('summon:user-choice-2-4');
  if (ctx.read.has(ability, 'user_choice_1_summon')) {
    const offered = new Set(ctx.read.entries(ability, 'user_choice_1_summon').map(layerListEntryUuid).filter((id) => UUID.test(id)));
    for (const v of [ctx.summonRequest?.pick, input.summonCreatureId, input.useAbilityVariable1, input.useAbilityVariable2]) {
      const id = upper(v);
      if (UUID.test(id) && offered.has(id)) return id;
    }
    refuse('Your summon selection choice is empty!');
  }
  const fixed = upper(layerListEntryUuid(ctx.read.value(ability, 'summon')));
  if (!UUID.test(fixed)) handBack('summon:creature-not-authored');
  return fixed;
}

/** The variable the user_choice_1_summon pick rides (janus.ts accepts it instead of handing the variable mode back). */
export function isSummonPickVariable(ctx: JanusCtx, ability: Row, variable1: unknown, variable2: unknown): boolean {
  if (!ctx.read.has(ability, 'user_choice_1_summon') || variable2 != null) return false;
  const offered = ctx.read.entries(ability, 'user_choice_1_summon').map(layerListEntryUuid);
  return UUID.test(upper(variable1)) && offered.includes(upper(variable1));
}

/** encounter_row_minion_team_uuid(): the controller's team; a team-less player character → Friend; else '' (the template's default). */
function minionTeam(ctx: JanusCtx, caster: Row): string {
  const raw = ecLean(caster, 'team') ?? ctx.read.value(caster, 'team');
  const team = upper(record(raw)?.items ? record(record(raw)?.items)?.item_id : raw);
  if (team) return team;
  const type = String(itemField(caster, 'item_type') ?? ecLean(caster, 'item_type') ?? '');
  return type === 'player_character' ? FRIEND : '';
}

/** PRE-DRAW, on state only: the shapes this lane does not serve hand back before any die. */
export function summonPreDraw(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row, targets: Row[]): void {
  if (!isSummonAbility(ctx, ability)) return;
  if (ctx.summons === undefined) handBack('post_effect_conditions:summon');
  const creatureId = summonCreatureId(ctx, input, ability);
  // the spot spacing reads the summoned creature's size — the page fetches a definition it lacks and resolves again
  if (!ctx.read.definition(creatureId)) handBack(`summon:creature-definition-not-held:${creatureId}`);
  if (targets.some(isSummonPointTarget)) {
    for (const key of Object.keys(record(ability.item_layer_types) ?? {}))
      if (/^(ability_works_only_against|ability_does_not_work_against|if_target_|status_effect_(applied|removed)_to_target|status_effect_removed_from_target|push_ft_after_hit|pull_ft_after_hit|ap_change_during_turn|reaction_reset)/.test(key))
        handBack(`summon:point-target-gate:${key}`);
    // the ground point itself (an explicit `<encounter>|x:y:z` selection never passed the FT3D placement's range check)
    const point = targets.find(isSummonPointTarget)!;
    const at = position(point);
    if (!at || !spotInRange(ctx, ability, caster)(at)) refuse('That point is out of range for this summon.');
  }
  // Creature-gated summons use the action's creature draft. Reject an incompatible body before costs or Grim Harvest;
  // uses_corpse marks the event and adds no death/freshness/consumption predicate.
  if (CT_GATE_KEYS.some(key => ctx.read.has(ability, key)) && targets.some(target => !creatureTypeGateAllows(ctx, ability, target)))
    refuse('This summon cannot affect this creature type.');
  // Summon Roll carriers apply to every summon; missing browser dispatch refuses before draws.
  if (!ctx.trigger) {
    const event: TriggerEvent = { trigger: CMRT, rollType: SUMMON_ROLL };
    const { carriers, statuses } = triggerCall(() => collectTriggerCandidates(janusCtxTriggerReaders(ctx), caster, event));
    if (carriers.length || statuses.length || ctx.read.entries(caster, CMRT).some((e) => upper(layerListEntryUuid(e)) === SUMMON_ROLL))
      handBack('summon:caster-makes-summon-roll-trigger');
  }
}

/** The ability's measured range for a ground spot: centre to centre on the floor plane, the caster's footprint and a half body credited —
 *  never stricter than the FT3D placement's rim measure. Self / Unlimited / Touch / an unknown range is not measured (true). */
function spotInRange(ctx: JanusCtx, ability: Row, caster: Row): (p: SummonPoint) => boolean {
  const raw = scalar(ctx.read.value(ability, 'range'));
  const rangeFt = RANGE_FT[upper(raw)] ?? (/^\d+$/.test(String(raw ?? '')) ? Number(raw) : null);
  const from = position(caster);
  const base = Number(record(record(record(caster.computed)?.creature)?.size)?.base ?? 5);
  const credit = (Number.isFinite(base) && base > 0 ? base : 5) / 2 + 2.5;
  return (p) => rangeFt === null || !from || Math.hypot(p.x - from.x, p.y - from.y) <= rangeFt + credit;
}

/** The fallback spot of summon #index: square rings around the anchor (orthogonal neighbours before diagonals), one body-width apart,
 *  skipping a cell that overlaps a chosen spot or (ring > 0) lies beyond the ability's measured range — janus_summon_desired_point(). */
function fallbackSpot(anchor: SummonPoint, chosen: SummonPoint[], index: number, spacing: number, inRange: (p: SummonPoint) => boolean): SummonPoint {
  let surviving = 0;
  for (let ring = 0; ring <= 8; ring++) {
    const cells: Array<[number, number]> = [];
    for (let dx = -ring; dx <= ring; dx++) for (let dy = -ring; dy <= ring; dy++) if (Math.max(Math.abs(dx), Math.abs(dy)) === ring) cells.push([dx, dy]);
    cells.sort((a, b) => (Math.abs(a[0]) + Math.abs(a[1])) - (Math.abs(b[0]) + Math.abs(b[1])));
    cells: for (const [dx, dy] of cells) {
      const cell = { x: anchor.x + dx * spacing, y: anchor.y + dy * spacing, z: anchor.z };
      for (const spot of chosen)
        if (Math.abs(spot.x - cell.x) < spacing - 0.01 && Math.abs(spot.y - cell.y) < spacing - 0.01 && Math.abs(spot.z - cell.z) < spacing) continue cells;
      if (ring > 0 && !inRange(cell)) continue;
      if (surviving++ === index) return cell;
    }
  }
  return anchor; // a huge batch in a tiny range: the cast point itself was already judged by the placement
}

/** janus_summon() — ONE summoned creature (#index of this cast), decided here and handed to the commit. */
export function planOneSummon(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row, target: Row, index: number): void {
  if (ctx.summons == null) handBack('post_effect_conditions:summon');
  if (ctx.summons.length >= SUMMON_POINTS_MAX) handBack('summon:too-many');
  const creatureId = summonCreatureId(ctx, input, ability);
  const creature = ctx.read.definition(creatureId);
  if (!creature) handBack(`summon:creature-definition-not-held:${creatureId}`);
  const mt = upper(layerListEntryUuid(ctx.read.value(ability, 'summon_monster_template')));
  const summonType = upper(layerListEntryUuid(ctx.read.value(ability, 'summon_type')));
  const tagged = ctx.read.has(ability, 'maintained_by_ability') || ctx.read.has(ability, 'spell_concentration')
    || (ctx.read.has(ability, 'duration_int') && ctx.read.has(ability, 'duration_type'));
  const ends = tagged ? getStatusEffectDurationTimes(ctx, ability, caster) : null;
  const plan: SummonPlan = {
    item_id: creatureId, monster_template_id: UUID.test(mt) ? mt : null, summon_type_id: UUID.test(summonType) ? summonType : null,
    team: minionTeam(ctx, caster), point: null, corpse: null,
    tag: ends ? { ends: ends.ends, flags: ends.flags, connection: ctx.statusConnectedId ??= ctx.read.uuid() } : null,
    in_combat: ctx.read.inCombat(ctx.encounter, caster) ? 1 : 0, after_row: ctx.rows.length,
  };
  if (index === 0 && isSummonPointTarget(target) && ctx.read.has(ability, 'maintained_by_ability')) plan.maintaining_source = true;
  if (ctx.trigger || ctx.summonOrigin?.abilityId !== upper(input.abilityId) || ctx.summonOrigin?.casterId !== ecId(caster)) {
    plan.caster_id = ecId(caster); plan.ability_id = upper(input.abilityId);
  }
  // CORPSE-RAISER (summon_transforms_targeted_corpse): the summon IS the targeted dead creature's body — it rises where it fell, and the
  // body is consumed. A body an earlier summon of this cast already raised (a multi-summon roll) leaves the rest to rise beside it.
  const corpseId = ecId(target);
  if (ctx.read.has(ability, 'summon_transforms_targeted_corpse') && corpseId && !isSummonPointTarget(target)) {
    const dead = Number(scalar(ecLean(target, 'encounter_creature_dead')) ?? 0) === 1
      || ctx.read.statuses(target).some((s) => upper(s.status_item_id) === DEAD_STATUS);
    if (dead && !ctx.summons.some((s) => s.corpse === corpseId)) {
      if (String(itemField(target, 'item_type') ?? ecLean(target, 'item_type') ?? '') === 'player_character') handBack('summon:corpse-is-player-character');
      plan.corpse = corpseId;
      ctx.summons.push(plan);
      return;
    }
  }
  const anchor = position(target);
  if (!anchor) handBack('summon:target-position-not-held');
  const request = ctx.summonRequest ?? null;
  const chosen = request && isSummonPointTarget(target) ? request.points : [];
  const width = SIZE_WIDTH_FT[upper(layerListEntryUuid(ctx.read.value(creature, 'size')))] ?? 5;
  const spacing = Math.max(5, width);
  // the measured range of a fallback cell: centre to centre on the floor plane, the caster's footprint credited — never stricter than the
  // placement's own rim measure; an unmeasurable range (Self / Unlimited / Touch) keeps the ring
  const inRange = spotInRange(ctx, ability, caster);
  const own = ctx.summons.filter((s) => s.corpse === null && s.point !== null).length;
  const pick = chosen[own];
  plan.point = pick && validSummonPoint(pick) ? { x: pick.x, y: pick.y, z: pick.z } : fallbackSpot(anchor!, chosen, own, spacing, inRange);
  ctx.summons.push(plan);
}
// ── Wizard v9 next_summon_ap_override (Architect of Emergence: "the first creature you summon during combat costs 0 AP") ────────────────
// A status carrying next_summon_ap_override caps the AP of the caster's next SUMMON cast (lowest wins) and is consumed by it. Sanguine's
// next_spell_ap_override shape: the cast gate (ability-preconditions.ts), the debit (spendSpellAction / spendAbilityAction) and the sheet's
// AP display read the same carrier.
export const NEXT_SUMMON_AP_OVERRIDE = 'next_summon_ap_override';
export function summonApOverrideStatus(ctx: JanusCtx, caster: Row): { value: number; status: Row } | null {
  let best: { value: number; status: Row } | null = null;
  for (const status of activeStatuses(ctx, caster)) {
    const definition = ctx.read.definition(upper(status.status_item_id)) ?? (record(record(status.status_arr)?.item_layer_types) ? record(status.status_arr) : null);
    if (!definition) definitionNotHeld('next_summon_ap_override:status-definition-not-held', status.status_item_id);
    if (!ctx.read.has(definition!, NEXT_SUMMON_AP_OVERRIDE)) continue;
    const value = Number(scalar(ctx.read.value(definition!, NEXT_SUMMON_AP_OVERRIDE)));
    if (!Number.isFinite(value) || value < 0) continue;
    if (!best || value < best.value) best = { value: Math.trunc(value), status };
  }
  return best;
}
/** The AP a summon cast actually pays: an AP-paid summon (cost > 0 — a reaction / free summon pays none and keeps the status) consumes the
 *  lowest armed override status, whatever its value. */
export function summonApCost(ctx: JanusCtx, ability: Row, caster: Row, cost: number): number {
  if (!isSummonAbility(ctx, ability) || cost <= 0) return cost;
  const override = summonApOverrideStatus(ctx, caster);
  if (!override) return cost;
  removeStoredStatus(ctx, caster, override.status);
  return Math.min(cost, override.value);
}

// ── Wizard v9 triggers_when_caster_uses_corpse (Grim Harvest: +1 Mana) ───────────────────────────────────────────────────────────────────
// Fires the CASTER's carriers once after a player's cast that carries the `uses_corpse` marker, or whose summon raised / consumed a corpse.
// Not in a trigger frame (a triggered dispatch never re-fires it — no loop).
export const USES_CORPSE_EVENT: TriggerEvent = { trigger: 'triggers_when_caster_uses_corpse' };
export interface CorpseUsePlan { owner: Row; marked: boolean; corpsesBefore: number; wentOffBefore: number }
export function planCorpseUse(ctx: JanusCtx, ability: Row, caster: Row): CorpseUsePlan | null {
  if (ctx.trigger) return null;
  const marked = Number(scalar(ctx.read.value(ability, 'uses_corpse')) ?? 0) === 1;
  if (!marked && !ctx.read.has(ability, 'summon_transforms_targeted_corpse')) return null;
  triggerFamilyPreDraw(ctx, caster, USES_CORPSE_EVENT, undefined, caster);
  return { owner: caster, marked, corpsesBefore: (ctx.summons ?? []).filter((s) => s.corpse !== null).length, wentOffBefore: ctx.castsWentOff ?? 0 };
}
export function fireCorpseUse(ctx: JanusCtx, plan: CorpseUsePlan | null): void {
  if (!plan || ctx.counterspelled) return;
  const raised = (ctx.summons ?? []).filter((s) => s.corpse !== null).length > plan.corpsesBefore;
  // DRIVER RULING 2026-10-09 ("When you animate or use a corpse, you gain 1 Mana"): a marked cast uses its corpse whenever the cast GOES
  // OFF — it reached its roll dispatch with a target (past armor bending, a hostile miscast and the counterspell check), whatever the
  // targets' saves (a lost contest, half or no damage, an immune target still counts). A failed armor bending roll, a miscast, a
  // counterspell or a refused / target-less cast pays nothing. A raised corpse always counts.
  const wentOff = (ctx.castsWentOff ?? 0) > plan.wentOffBefore;
  if (!(plan.marked && wentOff) && !raised) return;
  const owner = ctx.members.get(ecId(plan.owner)) ?? plan.owner;
  runTriggerFamily(ctx, USES_CORPSE_EVENT, owner, owner, [owner], false);
}
